import "@assets/css/bootstrap-grid.css";
import SchemasField from "@/components/json-ld-schemas/SchemasField";
import {
  getJsonLdDefaults,
  validateJsonLdSchemas,
} from "@/components/json-ld-schemas/utils";
import { Spinner } from "@components/ui/spinner";
import { zodResolver } from "@hookform/resolvers/zod";
import { endpoint } from "@services/api.services";
import { METHODS } from "@services/interface.service";
import { useQueryClient } from "@tanstack/react-query";
import { SkeletonTwo } from "@ui/external/components/skeleton";
import { getInputField } from "@utils/function/index.function";
import {
  isEditorContentEmpty,
  showToast,
  throwErrorAlert,
} from "@utils/helper/index.helper";
import { useHandleAction, usePathname } from "@utils/hook/index.hook";
import { faqFormData, faqSchema } from "@utils/interface/form.schema";
import { IndividualQuery } from "@utils/interface/index.interface";
import { faqData } from "@utils/interface/journey.interface";
import { useListData, usePostData } from "@utils/query/index.query";
import { usePending, useSelectionStore } from "@utils/states/index.state";
import React, { useEffect, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useParams } from "react-router";

const FAQForm: React.FC = () => {
  const { searchParams } = useHandleAction({});
  const { type } = useParams();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const {
    handleSubmit,
    control,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<faqFormData>({
    mode: "onChange",
    resolver: zodResolver(faqSchema),
    defaultValues: {
      json_ld: [],
    },
  });
  const { data: fetchData, isPending: fetchPending } = useListData<
    IndividualQuery<faqData>
  >({
    endpoint: endpoint["faqs"],
    id: searchParams.get("modal-item") || "",
    key: "faqs",
    params: {
      id: searchParams.get("modal-item") || "",
    },
    enabled: searchParams.get("modal-item") ? true : false,
  });

  const { handleAction } = useHandleAction({});
  const handleMutate = (data: faqFormData & { id?: string }) => {
    if (searchParams.get("modal-item")) {
      data = {
        ...data,
        id: searchParams.get("modal-item") || "",
      };
    }
    return {
      data,
      endpoint: endpoint["faqs"],
      method: searchParams.get("modal-item") ? METHODS.PUT : METHODS.POST,
    };
  };
  const handleSuccess = async ({ status }: { data: any; status: number }) => {
    if ([200, 201].includes(status)) {
      queryClient.invalidateQueries({ queryKey: [`${pathname}-listing`] });
      queryClient.invalidateQueries({
        queryKey: [`${pathname}-listing-scroll`],
      });

      handleAction({ type: "modal-close" });
      showToast({
        description: `${pathname} ${status === 200 ? "Updated" : "Created"}`,
        type: "success",
        title: "Success",
      });
    } else {
      return new Error("Unknown API Error");
    }
  };
  const { setIsFormPending } = useSelectionStore();
  const handleError = async (error: any) => {
    if (error?.data?.status == 413) {
      showToast({
        description: "File Size Too Large",
        type: "error",
        title: "Error",
      });
    }
    setIsFormPending(false);

    const errorMessage = error?.data?.data;
    throwErrorAlert(error);
  };
  /* set default value */
  const { setIsRetrive }: any = usePending();

  useEffect(() => {
    setIsRetrive(searchParams.get("modal-item") ? fetchPending : false);
    if (fetchData?.status === 200 && !fetchPending) {
      const firstResult = fetchData?.data;
      if (firstResult) {
        if (searchParams.get("modal") == "add") {
          return reset({ json_ld: [] } as any);
        } else {
          // @ts-ignore
          reset(() => ({
            question: firstResult?.question || "",
            description: isEditorContentEmpty(firstResult?.description || "")
              ? ""
              : firstResult?.description,
            header_code: firstResult?.header_code || "",
            footer_code: firstResult?.footer_code || "",
            json_ld: getJsonLdDefaults(firstResult?.json_ld),
          }));
        }
      }
    }
  }, [fetchData?.data, fetchPending]);

  const { mutate, isPending } = usePostData();
  const onSubmit: SubmitHandler<faqFormData> = (data) => {
    clearErrors("json_ld");
    const jsonLdResult = validateJsonLdSchemas(
      (data as any).json_ld || [],
      setError as any,
    );

    if (!jsonLdResult.isValid) {
      showToast({
        title: "Invalid JSON-LD",
        description: `Fix the highlighted schema block (#${jsonLdResult.firstInvalidIndex + 1}) before saving.`,
        type: "error",
      });
      return;
    }

    mutate(handleMutate({
      ...data,
      json_ld: jsonLdResult.normalizedSchemas,
    }), {
      onSuccess: ({ data, status }) => handleSuccess({ data, status }),
      onError: (data) => handleError({ data }),
    });
  };

  const formSchema = useMemo(
    () => [
      {
        name: "question",
        label: "Question",
        colSpan: 12,
        type: "text",
        placeholder: "Enter Question",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
        name: "description",
        label: "Description",
        colSpan: 12,
        type: "editor",
        placeholder: "Enter Description",
        control: control,
        setValue: setValue,
        errors,
        className: `mb-[20px] min-w-full w-full`,
        visable: true,
      },
      {
      name: "header_code",
      label: "Header Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
    {
      name: "footer_code",
      label: "Footer Code ",
      colSpan: 12,
      type: "textarea",
      placeholder: "Description Here",
      control: control,
      setValue: setValue,
      errors,
      className: `mb-[20px] min-w-full  w-full`,
      visable: true,
    },
      {
        name: "json_ld",
        label: "SEO Schemas",
        colSpan: 12,
        type: "component",
        component: (
          <div className="border rounded-md p-4 bg-white">
            <h2 className="text-lg font-semibold mb-4">SEO Schemas</h2>
            <SchemasField control={control} errors={errors} name="json_ld" />
          </div>
        ),
        visable: true,
      },
      {
        type: "submit",
        value: `${searchParams.get("modal") || ""} ${(
          type || pathname
        )?.replace("-", " ")}`,
        className: "w-full h-[47px] mt-[32px] capitalize",
        colSpan: 12,
        variant: "default",
        endIcon: isPending ? <Spinner /> : null,
        disabled: isPending ? true : false,
        visable: true,
      },
    ],
    [control, errors, isPending, pathname, searchParams, setValue, type]
  );

  if (fetchPending && searchParams.get("modal-item"))
    return (
      <>
        <SkeletonTwo />
      </>
    );
  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} className="row w-full">
        {formSchema.map(
          (field, index) =>
            (field.visable || field.visable == undefined) && (
              <div
                key={index}
                className={`col-lg-${field.colSpan} col-12 lg:px-[12px] px-[8px]`}
              >
                {getInputField({ ...field })}
              </div>
            )
        )}
      </form>
    </>
  );
};

export default FAQForm;
